import type {Component, Editor} from 'grapesjs';
import {noop} from '@Utils/general.util';
import {getUniqueId} from '@Utils/string.util';
import {openErrorModal} from '@Modals/error-modal';
import {
  ADD_DESIGN_TO_EMAIL_TEMPLATE,
  EMAIL_MAKER_BLOCKS,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_PROPS,
  LOCAL_STORAGE_POSTER_ID_KEY,
  LOCAL_STORAGE_POSTER_PAGE_ID_KEY,
} from '@EmailMakerReact/email-maker-react.constants';
import type {EmailMakerPluginDeps} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {addCssRules} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/cssrules';
import {REFRESH_STYLES_AFTER_KEYMAPS} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/listeners/keymaps';
import {finishLoading} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/editor-helper';
import {fetchAllColorPalettePills, fetchAllColorPalettes, fetchAiRewriteRecentPrompt, removeAiEditHighlightsOnLoad} from '@EmailMakerReact/lib/email-maker-ai-library';
import {applyAutoUpdates, removeAutoUpdateParamsFromUrl} from '@EmailMakerReact/lib/email-maker-auto-update-library';
import {fetchRemoveBackgroundCredits} from '@EmailMakerReact/lib/email-maker-background-removal-library';
import {appendBlockOnClick} from '@EmailMakerReact/lib/email-maker-block-insert-library';
import {
  fixComponentFullWidthOverflowAolYahoo,
  fixVerticalAlignmentVisual,
  markShrinkWrapCells,
  setLegacyMailCompatibility,
  updateBgImageGmailCompatibility,
  updateButtonAolCompatibility,
  updateTableOutlookCompatibility,
} from '@EmailMakerReact/lib/email-maker-compatibility-library';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {dropRedundantStyleAttribute, normalizeStyleAttributesTree} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {addContentComponent, reloadModifiedDesign} from '@EmailMakerReact/lib/email-maker-design-library';
import {addReadOnlyFooter} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {syncGettyCountForComponents} from '@EmailMakerReact/lib/email-maker-getty-library';
import {setImageLoading} from '@EmailMakerReact/lib/email-maker-media-library';
import {copyMediaQueriesToResponsiveStyle} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {addPuppeteerListeners, showPendingMarketingPopups, syncTemplateNameFromStore} from '@EmailMakerReact/lib/email-maker-misc-library';
import {removeComponentLoadingOverlaysOnLoad, toggleLoadingOverlayForEmail} from '@EmailMakerReact/lib/email-maker-overlay-library';
import {attachPasteAsTextListener, sanitizeAllComponents} from '@EmailMakerReact/lib/email-maker-sanitize-library';
import {refreshSelection} from '@EmailMakerReact/lib/email-maker-selection-library';
import {collapseLinkWrappersOnLoad, setTargetBlankForAllLinks, splitSharedLinkWrappersOnLoad} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {
  canCurrentUserAutosaveTemplate,
  getCurrentIdEmailTemplate,
  hasEmailTemplateLoadFailed,
  setEmailMakerAutosaveEnabled,
} from '@EmailMakerReact/lib/email-maker-storage-library';
import {validateAllTbodyComponents} from '@EmailMakerReact/lib/email-maker-table-library';
import {fetchSignupForms, fetchSocialLinkSuggestions} from '@EmailMakerReact/lib/email-maker-social-signup-library';

/** GrapesJS core command that adds `gjs-dashed` to the canvas iframe body; the injected editor-canvas.css scopes every outline/hover/selection rule under it. */
const SW_VISIBILITY_COMMAND = 'sw-visibility';
/** Email-maker marker on the canvas body. editor-canvas.css is shared with the form maker, so email-maker-only canvas chrome (the top breathing room for the first element's selection border + add-element button) is scoped under this class. */
const REACT_CANVAS_BODY_CLASS = 'pmw-react-email-maker';
const ADD_DESIGN_MODAL_CLASS = '-modal-650';
const ADD_DESIGN_MODAL_CONTENT_SELECTOR = '.content';
const DROPPED_IMAGE_CONTENT_TYPE = 'image' as const;

interface CanvasDropData {
  content: ComponentDropContent[];
}

interface ComponentDropContent {
  type: string;
  attributes: Record<string, unknown>;
  file: File & Partial<Record<typeof EMAIL_MAKER_PROPS.DROP_IDENTIFIER, string>>;
}

export function initEditorListeners(editor: Editor, deps: EmailMakerPluginDeps): () => void {
  const teardowns: (() => void)[] = [];
  let isTornDown = false;

  // Teardowns may register after the master teardown runs (async load sequence); run them immediately to avoid leaks.
  const addTeardown = (teardown: () => void): void => {
    if (isTornDown) {
      teardown();
      return;
    }
    teardowns.push(teardown);
  };

  const runLoadSequence = async (): Promise<void> => {
    removeAutoUpdateParamsFromUrl();
    void fetchRemoveBackgroundCredits(editor);
    void fetchSignupForms();
    void fetchSocialLinkSuggestions();
    finishLoading();
    enableComponentOutlines(editor);
    addContentComponent(editor);
    // Establishes the no-stale-raw-`style`-attribute invariant over the loaded tree; the component:add listener keeps it true for later additions.
    normalizeStyleAttributesTree(editor);
    syncTemplateNameFromStore(editor, deps.store);
    syncGettyCountForComponents(editor);

    await addReadOnlyFooter(editor);
    // React unmount can destroy the editor while the footer fetch is in flight; the remaining steps must not run against a dead editor.
    if (isTornDown) {
      return;
    }

    addCssRules(editor, getCurrentIdEmailTemplate(editor));
    addDesignForDeprecatedPublishingFlow(editor);
    initAutosave(editor);
    setLegacyMailCompatibility(editor);
    markShrinkWrapCells(editor);
    updateBgImageGmailCompatibility(editor);
    updateButtonAolCompatibility(editor);
    fixComponentFullWidthOverflowAolYahoo(editor);
    sanitizeAllComponents(editor);
    setTargetBlankForAllLinks(editor);
    validateAllTbodyComponents(editor);
    updateTableOutlookCompatibility(editor);
    showPendingMarketingPopups();
    addTeardown(attachPasteAsTextListener(editor));
    copyMediaQueriesToResponsiveStyle(editor, getMainWrapper(editor), ['width']);
    fixVerticalAlignmentVisual(editor);

    toggleLoadingOverlayForEmail(editor, false);
    removeComponentLoadingOverlaysOnLoad(editor);
    removeAiEditHighlightsOnLoad(editor);
    splitSharedLinkWrappersOnLoad(editor);
    collapseLinkWrappersOnLoad(editor);
    fetchAllColorPalettes();
    fetchAllColorPalettePills();
    void applyAutoUpdates(editor, deps.store);
    void fetchAiRewriteRecentPrompt();

    editor.UndoManager.clear();
    addTeardown(addPuppeteerListeners(editor, deps.store));
  };

  const onLoad = (): void => {
    // The loading overlay stays up on failure; the catch is only rejection hygiene for the footer fetch.
    runLoadSequence().catch((error: unknown) => {
      console.error('Email maker load sequence failed:', error);
    });
  };

  const onUndoRedo = (): void => {
    refreshSelection(editor);
  };

  const onCanvasDragData = (_dataTransfer: DataTransfer, result: CanvasDropData): void => {
    setDroppedImagesMetaData(result);
  };

  const onCanvasDrop = (_dataTransfer: DataTransfer, model: Component | Component[]): void => {
    activateIfOneComponentDropped(model, editor);
  };

  const onKeymapEmit = (id: string): void => {
    if (REFRESH_STYLES_AFTER_KEYMAPS.includes(id)) {
      refreshSelection(editor);
    }
  };

  // Maintains the no-stale-raw-`style`-attribute invariant for every component added after load (paste, duplicate, block insert, AI content).
  const onComponentAdd = (component: Component): void => {
    dropRedundantStyleAttribute(component);
  };

  editor.on('load', onLoad);
  editor.on('undo redo', onUndoRedo);
  editor.on('canvas:dragdata', onCanvasDragData);
  editor.on('canvas:drop', onCanvasDrop);
  editor.on('keymap:emit', onKeymapEmit);
  editor.on('component:add', onComponentAdd);

  return (): void => {
    isTornDown = true;
    editor.off('load', onLoad);
    editor.off('undo redo', onUndoRedo);
    editor.off('canvas:dragdata', onCanvasDragData);
    editor.off('canvas:drop', onCanvasDrop);
    editor.off('keymap:emit', onKeymapEmit);
    editor.off('component:add', onComponentAdd);
    teardowns.forEach((teardown) => {
      teardown();
    });
  };
}

function enableComponentOutlines(editor: Editor): void {
  editor.runCommand(SW_VISIBILITY_COMMAND);
  editor.Canvas.getBody()?.classList.add(REACT_CANVAS_BODY_CLASS);
}

/**
 * Turns autosave on when the current user may autosave this template — but never after a failed load.
 * GrapesJS still fires `load` on a fetch failure, leaving an empty canvas while the session holds the
 * real template id, so an autosave would overwrite the saved template with an empty project.
 */
function initAutosave(editor: Editor): void {
  if (hasEmailTemplateLoadFailed(editor)) {
    console.error('Email maker template load failed; autosave stays disabled to protect the saved template.');
    return;
  }
  if (canCurrentUserAutosaveTemplate(editor)) {
    setEmailMakerAutosaveEnabled(editor, true);
  }
}

function activateIfOneComponentDropped(components: Component | Component[], editor: Editor): void {
  const droppedComponents = Array.isArray(components) ? components : [components];
  if (droppedComponents.length === 0) {
    return;
  }

  editor.select(droppedComponents[0]);

  droppedComponents.forEach((component: Component) => {
    if (isDroppedFileImage(component)) {
      setImageLoading(editor, {component, loading: true});
    }
  });
}

/**
 * Only dropped file-images should enter the loading state. `setDroppedImagesMetaData` stamps the
 * DROP_IDENTIFIER attribute on image drops alone, and the resolve/error paths clear the spinner via
 * that same identifier (`getComponentByLoadIdentifier`). Template/section/text/button drops never
 * resolve an image, so marking them loading would spin forever.
 */
function isDroppedFileImage(component: Component): boolean {
  return Boolean(component.getAttributes()[EMAIL_MAKER_PROPS.DROP_IDENTIFIER]);
}

function setDroppedImagesMetaData(result: CanvasDropData): void {
  if (!Array.isArray(result.content) || result.content.length === 0) {
    return;
  }

  for (const content of result.content) {
    if (content.type === DROPPED_IMAGE_CONTENT_TYPE) {
      const identifier = getUniqueId();
      content.type = EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE;
      content.attributes[EMAIL_MAKER_PROPS.DROP_IDENTIFIER] = identifier;
      content.file[EMAIL_MAKER_PROPS.DROP_IDENTIFIER] = identifier;
    }
  }
}

function addDesignForDeprecatedPublishingFlow(editor: Editor): void {
  const designId = window.localStorage.getItem(LOCAL_STORAGE_POSTER_ID_KEY);
  const pageId = window.localStorage.getItem(LOCAL_STORAGE_POSTER_PAGE_ID_KEY) ?? undefined;
  if (!designId) {
    return;
  }

  if (window.PMW.enableMyStuffMultipagesUI && !pageId) {
    console.error('No pageID found');
    return;
  }

  const buttons = getAddDesignToEmailTemplateModalButtons(editor, designId, pageId);
  const dialogContent = window.PMW.openModalDialog(ADD_DESIGN_TO_EMAIL_TEMPLATE, window.i18next.t('pmwjs_add_design_template'), buttons, true, ADD_DESIGN_MODAL_CLASS, false).find(
    ADD_DESIGN_MODAL_CONTENT_SELECTOR
  );

  window.PMW.load(dialogContent, window.PMW.util.site_url(`emailmaker/addDesignToEmailTemplateDialog/${designId}${pageId ? '/' + pageId : ''}`), {}, (): void => {
    window.localStorage.removeItem(LOCAL_STORAGE_POSTER_ID_KEY);
    window.localStorage.removeItem(LOCAL_STORAGE_POSTER_PAGE_ID_KEY);
    if (!document.getElementById('add-design-template')) {
      console.error('Poster with design ID not found');
      void window.PMW.closeModalDialog(ADD_DESIGN_TO_EMAIL_TEMPLATE).then((): void => {
        openErrorModal({
          message: window.i18next.t('pmwjs_adding_design_error'),
        });
      }, noop);
    }
  });
}

function addDesignWithId(editor: Editor, id: string, pageId?: string): void {
  const block = editor.BlockManager.get(EMAIL_MAKER_BLOCKS.ID_DESIGN);
  const isActivatedOnLoad = block.get('activate');

  block.set('activate', false);
  const component = appendBlockOnClick(editor, block, 0);
  component?.set(EMAIL_MAKER_PROPS.DESIGN_ID, id);
  component?.set(EMAIL_MAKER_PROPS.DESIGN_PAGE_ID, pageId);
  block.set('activate', isActivatedOnLoad);

  reloadModifiedDesign(editor, component);
}

function getAddDesignToEmailTemplateModalButtons(editor: Editor, designId: string, designPageId?: string): Record<string, {ch: VoidFunction; classes: string}> {
  const button: Record<string, {ch: VoidFunction; classes: string}> = {};
  button[window.i18next.t('pmwjs_no_dont_add')] = {
    ch: (): void => {
      void window.PMW.closeModalDialog(ADD_DESIGN_TO_EMAIL_TEMPLATE);
    },
    classes: '-go-back secondary',
  };
  button[window.i18next.t('pmwjs_yes_add_my_design')] = {
    ch: (): void => {
      addDesignWithId(editor, designId, designPageId);
      void window.PMW.closeModalDialog(ADD_DESIGN_TO_EMAIL_TEMPLATE);
    },
    classes: '-add-design',
  };
  return button;
}
